// src/ManageTeachers.js
import React, { useEffect, useState } from 'react';
import API from './api';
import {
  FaEdit,
  FaMapMarkerAlt,
  FaHistory,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaSave,
  FaTimes,
  FaCheck,
  FaMinus,
  FaKey,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

import UserTaxonomyEditor from './UserTaxonomyEditor';
import { saveTeacherTaxonomy } from './api';

const teacherIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const FieldError = ({ error }) =>
  error ? <span className="text-red-500 text-xs ml-1">{error}</span> : null;

const initialForm = {
  id: null,
  name: '',
  email: '',
  password: '',
  is_active: true,
  is_available: true,
  pay_first_commute: false,

  // Rates
  hourly_rate: '',          // Visit hourly rate (DB: hourly_rate)
  office_hourly_rate: '',   // Office hourly rate (DB: office_hourly_rate)
  drive_hourly_rate: '',    // Drive hourly rate (DB: drive_hourly_rate)

  travel_cost_per_km: '',
  bonus: '',
  years_experience: '',
  max_travel_miles: '',
  max_caseload: '',
  gender: 'other',
  latitude: '',
  longitude: '',
  personal_phone: '',
  personal_email: '',
  company_phone: '',
  company_email: '',
  employment_start_date: '',
  employment_end_date: '',
  admin_notes: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: '',
  // training
  training_stage: 'none', // 'none' | 'shadow' | 'supervised'
  mentor_teacher_id: '',
  training_start_date: '', // NOVO: datum početka treninga (YYYY-MM-DD)
  training_notes: '',      // NOVO: interni komentari o treningu
};

// --- helper za discipline bedževe ---
const disciplineMeta = {
  PROG_EI: {
    label: 'EI',
    classes: 'bg-blue-100 text-blue-700 border border-blue-300',
  },
  PROG_PI: {
    label: 'PI',
    classes: 'bg-orange-100 text-orange-700 border border-orange-300',
  },
  PROG_ABA: {
    label: 'ABA',
    classes: 'bg-purple-100 text-purple-700 border border-purple-300',
  },
  PROG_OT: {
    label: 'OT',
    classes: 'bg-teal-100 text-teal-700 border border-teal-300',
  },
  PROG_PT: {
    label: 'PT',
    classes: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
  },
  PROG_ST: {
    label: 'ST',
    classes: 'bg-green-100 text-green-700 border border-green-300',
  },
};

function renderDisciplineBadges(codes) {
  if (!Array.isArray(codes) || !codes.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {codes.map((code) => {
        const meta = disciplineMeta[code] || {
          label: code.replace(/^PROG_/, ''),
          classes: 'bg-gray-100 text-gray-700 border border-gray-300',
        };
        return (
          <span
            key={code}
            className={
              'px-1.5 py-0.5 rounded-full text-[10px] uppercase font-semibold ' +
              meta.classes
            }
          >
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

// --- helper za training bedž (SAMO badge) ---
const trainingMeta = {
  none: {
    label: 'Regular',
    classes: 'bg-gray-100 text-gray-700 border border-gray-300',
  },
  shadow: {
    label: 'Shadow',
    classes: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  },
  supervised: {
    label: 'Supervised',
    classes: 'bg-indigo-100 text-indigo-800 border border-indigo-300',
  },
};

function renderTrainingBadge(stageRaw) {
  const stage = (stageRaw || 'none').toLowerCase();
  const meta = trainingMeta[stage] || trainingMeta.none;
  return (
    <span
      className={
        'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ' +
        meta.classes
      }
    >
      {meta.label}
    </span>
  );
}

function ManageTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTeacherForAudit, setActiveTeacherForAudit] = useState(null);
  const [mapTeacherLocation, setMapTeacherLocation] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [taxo, setTaxo] = useState({ strengths: [], limitations: [] });

  // filter: all | active | inactive | training
  const [filterMode, setFilterMode] = useState('active');

  // CSV import modal state
  const [importErrors, setImportErrors] = useState([]);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(null);
  const [importFailCount, setImportFailCount] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);

  // Magic login modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectedTeacherForLogin, setSelectedTeacherForLogin] = useState(null);
  const [sendingLogin, setSendingLogin] = useState(false);

  const isEditMode = !!formData.id;

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const res = await API.get('/teachers');
      setTeachers(res.data || []);
    } catch (err) {
      toast.error('Cannot load teachers');
    }
  };

  const requiredFields = [
    'name',
    'email',
    'address_street',
    'address_city',
    'address_state',
    'address_zip',
    'address_country',
  ];

  const validate = () => {
    let errs = {};
    for (let f of requiredFields) if (!formData[f]) errs[f] = 'Required';

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Invalid format';
    }

    if (!formData.id && !formData.password) {
      errs.password = 'Password required';
    } else if (formData.id && formData.password && formData.password.length < 6) {
      errs.password = 'Min 6 chars';
    }

    // mentor je obavezan za shadow/supervised
    if (
      formData.training_stage !== 'none' &&
      (!formData.mentor_teacher_id || String(formData.mentor_teacher_id).trim() === '')
    ) {
      errs.mentor_teacher_id = 'Mentor required for training stage';
    }

    setErrors(errs);

    if (errs.mentor_teacher_id) {
      toast.error('Please select a mentor for Shadow/Supervised training stage.');
    }

    return Object.keys(errs).length === 0;
  };

  const openEditModal = async (teacher = null) => {
    setFormData(
      teacher
        ? {
            ...initialForm,
            ...teacher,
            bonus: teacher.bonus || '',
            password: '',
            pay_first_commute: !!teacher.pay_first_commute,
            years_experience: teacher.years_experience ?? '',
            max_travel_miles: teacher.max_travel_miles ?? '',
            max_caseload: teacher.max_caseload ?? '',
            gender: teacher.gender || 'other',
            training_stage: teacher.training_stage || 'none',
            mentor_teacher_id: teacher.mentor_teacher_id
              ? String(teacher.mentor_teacher_id)
              : '',
            // rates (NEW)
            hourly_rate: teacher.hourly_rate ?? '',
            office_hourly_rate: teacher.office_hourly_rate ?? '',
            drive_hourly_rate: teacher.drive_hourly_rate ?? '',
            // ako BE šalje ove vrednosti, ovde ih samo preuzimamo
            training_start_date: teacher.training_start_date || '',
            training_notes: teacher.training_notes || '',
          }
        : initialForm
    );
    setErrors({});

    if (!teacher) {
      // default: EI kao početna disciplina
      setTaxo({
        strengths: [{ code: 'PROG_EI', level: 'basic', weight: 1 }],
        limitations: [],
      });
    } else {
      setTaxo({ strengths: [], limitations: [] });
    }

    setModalOpen(true);

    if (teacher?.id) {
      try {
        const { data } = await API.get(`/teachers/${teacher.id}/taxonomy`);
        setTaxo({
          strengths: data?.strengths || [],
          limitations: data?.limitations || [],
        });
      } catch {
        // ignore
      }
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    // posebna logika za training_stage da čistimo mentora kad je 'none'
    if (name === 'training_stage') {
      const newStage = value;
      setFormData((f) => ({
        ...f,
        training_stage: newStage,
        mentor_teacher_id: newStage === 'none' ? '' : f.mentor_teacher_id,
      }));
      // takođe obriši eventualnu staru grešku za mentora
      setErrors((prev) => {
        const { mentor_teacher_id, ...rest } = prev;
        return rest;
      });
      return;
    }

    setFormData((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const strengthsNow = (taxo.strengths || []).map((s) =>
      typeof s === 'string' ? { code: s, level: 'basic', weight: 1 } : s
    );

    // MUST have at least one program (any PROG_*)
    const hasAnyProgram = strengthsNow.some((s) => {
      const code = String(s?.code || '').toUpperCase().trim();
      return code.startsWith('PROG_'); // EI/PI/ABA/OT/PT/ST + future
    });

    if (!hasAnyProgram) {
      toast.error('At least one program must be selected (EI, PI, ABA, OT, PT, ST).');
      return;
    }

    setLoading(true);
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      data.pay_first_commute = !!data.pay_first_commute;

      // mentor ima smisla samo kada je training_stage != 'none'
      if (data.training_stage === 'none') {
        data.mentor_teacher_id = null;
      } else if (!data.mentor_teacher_id) {
        data.mentor_teacher_id = null;
      }

      const method = formData.id ? API.put : API.post;
      const url = formData.id ? `/teachers/${formData.id}` : '/teachers';
      const res = await method(url, data);

      const teacherId = formData.id ?? res.data?.id;

      if (teacherId) {
        const strengthsPayload = strengthsNow.map((x) =>
          typeof x === 'string'
            ? { code: x, level: 'basic', weight: 1 }
            : { code: x.code, level: x.level, weight: x.weight }
        );
        const limitationsPayload = (taxo.limitations || []).map((x) =>
          typeof x === 'string' ? { code: x } : { code: x.code }
        );

        try {
          await saveTeacherTaxonomy(teacherId, {
            strengths: strengthsPayload,
            limitations: limitationsPayload,
          });
        } catch {
          // ignore taxonomy save failure here
        }
      }

      toast.success('Teacher saved!');
      if (!formData.id) {
        toast.info(
          'Tip: open Edit to set Profile (Strengths / Limitations) for this teacher.'
        );
      }

      setModalOpen(false);
      fetchTeachers();
    } catch (err) {
      if (err.response?.data?.missing) {
        let errs = {};
        for (let f of err.response.data.missing) errs[f] = 'Required';
        setErrors(errs);
      }
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveStatus = async (teacher) => {
    try {
      const storedUser = localStorage.getItem('user');
      const user_id = storedUser ? JSON.parse(storedUser).id : null;
      await API.patch(`/teachers/${teacher.id}`, {
        is_active: !teacher.is_active,
        user_id,
      });
      fetchTeachers();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const loadAuditLog = async (teacherId) => {
    try {
      const res = await API.get(`/teachers/${teacherId}/audit`);
      setAuditLog(res.data);
      setActiveTeacherForAudit(teacherId);
    } catch (err) {
      toast.error('Cannot load history');
    }
  };

  const showTeacherLocation = async (teacherId) => {
    try {
      const res = await API.get(`/teachers/${teacherId}/last-location`);
      setMapTeacherLocation({
        lat: res.data.lat,
        lon: res.data.lon,
        name: teachers.find((t) => t.id === teacherId)?.name || '',
      });
      setMapModalOpen(true);
    } catch {
      toast.info('No location found');
    }
  };

  // MAGIC LOGIN: open confirm modal
  const openSendLoginModal = (teacher) => {
    setSelectedTeacherForLogin(teacher);
    setLoginModalOpen(true);
  };

  // MAGIC LOGIN: call API
  const handleSendLoginLink = async () => {
    if (!selectedTeacherForLogin) return;
    setSendingLogin(true);
    try {
      await API.post(`/teachers/${selectedTeacherForLogin.id}/send-login-link`);
      toast.success(`Login link & QR code sent to ${selectedTeacherForLogin.email}`);
      setLoginModalOpen(false);
      setSelectedTeacherForLogin(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send login link');
    } finally {
      setSendingLogin(false);
    }
  };

  // CSV IMPORT
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported!');
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        setImportLoading(true);
        setImportProgress(0);
        setImportTotal(results.data.length);
        try {
          let success = 0;
          let failed = 0;
          let allResults = [];
          for (let i = 0; i < results.data.length; i++) {
            setImportProgress(i + 1);
            try {
              const row = { ...results.data[i] };
              if ('pay_first_commute' in row) {
                const s = String(row.pay_first_commute || '').trim().toLowerCase();
                row.pay_first_commute =
                  s === '1' || s === 'true' || s === 'yes' || s === 'da' || s === 'y'
                    ? 1
                    : 0;
              }
              const res = await API.post('/teachers/import-csv', row);
              const importResult = Array.isArray(res.data.results)
                ? res.data.results[0]
                : res.data;
              if (importResult && importResult.success) success++;
              else failed++;
              allResults.push(
                importResult || {
                  row: results.data[i],
                  success: false,
                  error: 'Unknown error',
                }
              );
            } catch (err) {
              failed++;
              allResults.push({
                row: results.data[i],
                success: false,
                error: err.response?.data?.error || err.message,
              });
            }
          }
          setImportSuccessCount(success);
          setImportFailCount(failed);
          const errors = allResults.filter((r) => !r.success);
          if (errors.length > 0) {
            setImportErrors(errors);
            setShowImportErrorModal(true);
          } else {
            setImportErrors([]);
            setShowImportErrorModal(false);
          }
          toast.success(`Imported: ${success}, Failed: ${failed}`);
          fetchTeachers();
        } catch (err) {
          toast.error('Import failed: ' + (err.response?.data?.error || err.message));
        }
        setImportLoading(false);
        setImportProgress(0);
        setImportTotal(0);
      },
    });
  };

  // CSV EXPORT
  const handleExportCSV = async () => {
    try {
      const res = await API.get('/teachers/export');
      const csv = Papa.unparse(res.data);
      const blob = new Blob([csv], { type: 'text/csv' });
      saveAs(blob, 'teachers_export.csv');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  // Filter logika
  const filteredTeachers = teachers.filter((t) => {
    if (filterMode === 'active') return !!t.is_active;
    if (filterMode === 'inactive') return !t.is_active;
    if (filterMode === 'training')
      return t.training_stage && t.training_stage !== 'none';
    return true; // all
  });

  // Lista mentora: aktivni, regular (bez training stage), osim samog sebe
  const mentorOptions = teachers.filter(
    (t) =>
      t.is_active &&
      (t.training_stage || 'none') === 'none' &&
      (!formData.id || t.id !== formData.id)
  );

  const filterBtnBase =
    'px-2 py-1 rounded-full text-[11px] md:text-xs border transition-colors';
  const activeFilterClasses = 'bg-sky-700 text-white border-sky-700 shadow-sm';
  const inactiveFilterClasses =
    'bg-white text-slate-600 border-slate-300 hover:bg-slate-100';

  // helper da izvučemo ime mentora za listu (koristi se i u mobile i u desktop prikazu)
  const getMentorName = (t) => {
    if (!t) return '';
    if (t.mentor_name) return t.mentor_name;
    if (t.mentor_teacher_id) {
      const mentor = teachers.find((x) => x.id === t.mentor_teacher_id);
      return mentor?.name || '';
    }
    return '';
  };

  return (
    <div className="p-1">
      <ToastContainer position="top-center" />

      {/* Header */}
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h2 className="text-lg font-semibold">Manage Teachers</h2>

        <div className="flex flex-col gap-2 items-stretch md:flex-row md:items-center md:gap-3">
          {/* Filter buttons */}
          <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-1 py-0.5 self-start md:self-auto">
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'all' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('all')}
            >
              All
            </button>
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'active' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('active')}
            >
              Active
            </button>
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'inactive' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('inactive')}
            >
              Inactive
            </button>
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'training' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('training')}
            >
              Training
            </button>
          </div>

          {/* Export / Import / Add */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="bg-green-600 text-white px-2 py-1 rounded text-xs"
            >
              Export CSV
            </button>
            <label className="bg-sky-600 text-white px-2 py-1 rounded cursor-pointer text-xs">
              Import CSV
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleImportCSV}
                className="hidden"
              />
            </label>
            <button
              onClick={() => openEditModal()}
              className="bg-sky-700 text-white px-2 py-1 rounded flex items-center gap-1 text-xs"
            >
              <FaPlus /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Import loading overlay */}
      {importLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[280px] flex flex-col items-center">
            <span className="text-lg font-semibold mb-2">Importing data...</span>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="h-4 rounded-full transition-all bg-blue-500"
                style={{
                  width: importTotal
                    ? `${Math.round((importProgress / importTotal) * 100)}%`
                    : '0%',
                }}
              />
            </div>
            <span className="text-sm text-gray-700">
              {importProgress} / {importTotal}
            </span>
            <span className="text-xs text-gray-400 mt-2">
              This may take a while due to address geocoding...
            </span>
          </div>
        </div>
      )}

      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {filteredTeachers.map((t, index) => {
          const mentorName = getMentorName(t);
          const showMentorLine =
            t.training_stage && t.training_stage !== 'none' && mentorName;
          return (
            <div key={t.id} className="bg-white rounded shadow p-3 flex flex-col">
              <div className="flex justify-between">
                <div>
                  <span className="font-semibold">
                    {index + 1}. {t.name}
                  </span>
                  <div className="text-xs">{t.email}</div>

                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {t.is_active ? (
                      <FaUserCheck className="text-green-600" />
                    ) : (
                      <FaUserTimes className="text-red-600" />
                    )}

                    <span className="text-xs">Visit: {t.hourly_rate}$ /h</span>
                    <span className="text-xs">Office: {t.office_hourly_rate}$ /h</span>
                    <span className="text-xs">Drive: {t.drive_hourly_rate}$ /h</span>
                    <span className="text-xs">{t.travel_cost_per_km}$ /mile</span>
                    <span className="text-xs">{t.bonus || 0}$ bonus</span>
                  </div>

                  {/* discipline badges (mobile) */}
                  {renderDisciplineBadges(t.disciplines)}
                  {/* training badge + mentor (mobile) */}
                  <div className="mt-1 flex flex-col items-start">
                    {renderTrainingBadge(t.training_stage)}
                    {showMentorLine && (
                      <span className="text-[10px] text-slate-500 mt-0.5">
                        by {mentorName}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <button onClick={() => openEditModal(t)} className="text-blue-600">
                    <FaEdit />
                  </button>
                  <button onClick={() => toggleActiveStatus(t)}>
                    {t.is_active ? (
                      <FaUserTimes className="text-red-600" />
                    ) : (
                      <FaUserCheck className="text-green-600" />
                    )}
                  </button>
                  <button onClick={() => loadAuditLog(t.id)}>
                    <FaHistory />
                  </button>
                  <button onClick={() => showTeacherLocation(t.id)}>
                    <FaMapMarkerAlt />
                  </button>
                  <button
                    onClick={() => openSendLoginModal(t)}
                    title="Send login link & QR"
                  >
                    <FaKey className="text-amber-600" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full border text-xs md:text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Name</th>
              <th className="border p-2">Email</th>
              <th className="border p-2">Gender</th>
              <th className="border p-2">Active</th>
              <th className="border p-2">Available</th>

              <th className="border p-2">Visit $/h</th>
              <th className="border p-2">Office $/h</th>
              <th className="border p-2">Drive $/h</th>

              <th className="border p-2">$/mile</th>
              <th className="border p-2">Bonus</th>
              
              {/* kolona za discipline */}
              <th className="border p-2">Disciplines</th>
              {/* kolona za training */}
              <th className="border p-2">Training</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTeachers.map((t, index) => {
              const mentorName = getMentorName(t);
              const showMentorLine =
                t.training_stage && t.training_stage !== 'none' && mentorName;
              return (
                <tr
                  key={t.id}
                  className={t.is_active ? '' : 'bg-gray-100 text-gray-500'}
                >
                  <td className="border p-2">
                    {index + 1}. {t.name}
                  </td>
                  <td className="border p-2">{t.email}</td>
                  <td className="border p-2 capitalize">{t.gender || 'other'}</td>
                  <td className="border p-2 text-center">
                    {t.is_active ? (
                      <FaUserCheck className="text-green-600" />
                    ) : (
                      <FaUserTimes className="text-red-600" />
                    )}
                  </td>
                  <td className="border p-2 text-center">
                    {t.is_available ? <FaUserCheck /> : <FaUserTimes />}
                  </td>

                  <td className="border p-2">{t.hourly_rate}$</td>
                  <td className="border p-2">{t.office_hourly_rate}$</td>
                  <td className="border p-2">{t.drive_hourly_rate}$</td>

                  <td className="border p-2">{t.travel_cost_per_km}$</td>
                  <td className="border p-2">{t.bonus || 0}$</td>
                  
                  {/* discipline badges (desktop) */}
                  <td className="border p-2">
                    {renderDisciplineBadges(t.disciplines)}
                  </td>
                  {/* training badge + mentor (desktop) */}
                  <td className="border p-2 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      {renderTrainingBadge(t.training_stage)}
                      {showMentorLine && (
                        <span className="text-[10px] text-slate-500">
                          by {mentorName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="border p-2 text-center space-x-2">
                    <button
                      onClick={() => openEditModal(t)}
                      title="Edit"
                      className="text-blue-600"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => toggleActiveStatus(t)}
                      title="Toggle Active"
                    >
                      {t.is_active ? (
                        <FaUserTimes className="text-red-600" />
                      ) : (
                        <FaUserCheck className="text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={() => loadAuditLog(t.id)}
                      title="History"
                    >
                      <FaHistory />
                    </button>
                    <button
                      onClick={() => showTeacherLocation(t.id)}
                      title="Last Location"
                    >
                      <FaMapMarkerAlt />
                    </button>
                    <button
                      onClick={() => openSendLoginModal(t)}
                      title="Send login link & QR"
                    >
                      <FaKey className="text-amber-600" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white max-h-[95vh] overflow-y-auto p-6 rounded shadow w-[97%] max-w-xl">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              {formData.id ? <FaEdit /> : <FaPlus />}{' '}
              {formData.id ? 'Edit' : 'Add'} Teacher
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic info */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Basic information
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Full name *
                    </label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Jane Doe"
                      className={`w-full border p-2 rounded text-sm ${
                        errors.name ? 'border-red-500' : ''
                      }`}
                    />
                    <FieldError error={errors.name} />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Email *
                    </label>
                    <input
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="name@example.com"
                      className={`w-full border p-2 rounded text-sm ${
                        errors.email ? 'border-red-500' : ''
                      }`}
                    />
                    <FieldError error={errors.email} />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-[11px] font-medium text-slate-600 block mb-1">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender || 'other'}
                    onChange={handleChange}
                    className="w-full border p-2 rounded text-sm"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Account & Status */}
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Account & status
                </h4>
                <div>
                  <label className="text-[11px] font-medium text-slate-600 block mb-1">
                    Password {formData.id ? '(optional)' : '*'}
                  </label>
                  <input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={
                      formData.id
                        ? 'New password (leave empty to keep current)'
                        : 'Set initial password'
                    }
                    className={`w-full border p-2 rounded text-sm ${
                      errors.password ? 'border-red-500' : ''
                    }`}
                  />
                  {formData.id && (
                    <div className="text-xs text-slate-500 mt-1">
                      Leave empty to keep existing password.
                    </div>
                  )}
                  <FieldError error={errors.password} />
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />{' '}
                    Active account
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="is_available"
                      checked={formData.is_available}
                      onChange={handleChange}
                    />{' '}
                    Available for scheduling
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="pay_first_commute"
                      checked={!!formData.pay_first_commute}
                      onChange={handleChange}
                    />
                    Pay first commute
                  </label>
                </div>
              </div>

              {/* Training */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Training
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Training stage
                    </label>
                    <select
                      name="training_stage"
                      value={formData.training_stage || 'none'}
                      onChange={handleChange}
                      className="w-full border p-2 rounded text-xs"
                    >
                      <option value="none">Regular (no training)</option>
                      <option value="shadow">Shadow</option>
                      <option value="supervised">Supervised</option>
                    </select>
                    <div className="text-[11px] text-gray-500 mt-1">
                      Use <b>Shadow</b> and <b>Supervised</b> for training staff. Regular
                      means fully independent provider.
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Mentor (only for training)
                    </label>
                    <select
                      name="mentor_teacher_id"
                      value={formData.mentor_teacher_id}
                      onChange={handleChange}
                      disabled={formData.training_stage === 'none'}
                      className="w-full border p-2 rounded text-xs disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <option value="">No mentor / Not assigned</option>
                      {mentorOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.email})
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.mentor_teacher_id} />
                    <div className="text-[11px] text-gray-500 mt-1">
                      Mentor must be an active{' '}
                      <span className="font-semibold">regular</span> teacher. When the
                      teacher becomes Regular, mentor is cleared automatically.
                    </div>
                  </div>
                </div>

                {/* dodatna polja za training_start_date i training_notes */}
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Training start date
                    </label>
                    <input
                      type="date"
                      name="training_start_date"
                      value={formData.training_start_date || ''}
                      onChange={handleChange}
                      className="w-full border p-2 rounded text-sm"
                    />
                    <div className="text-[11px] text-gray-500 mt-1">
                      If left empty, we can auto-fill when the teacher enters Shadow /
                      Supervised stage.
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Training notes (internal)
                    </label>
                    <textarea
                      name="training_notes"
                      value={formData.training_notes || ''}
                      onChange={handleChange}
                      rows={2}
                      className="w-full border p-2 rounded text-sm"
                      placeholder="Optional internal notes about training progress."
                    />
                  </div>
                </div>
              </div>

              {/* Compensation & workload */}
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Compensation & workload
                </h4>

                {/* NEW: three hourly rate fields */}
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Visit hourly rate ($/h)
                    </label>
                    <input
                      name="hourly_rate"
                      value={formData.hourly_rate}
                      onChange={handleChange}
                      placeholder="e.g. 65"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Office hourly rate ($/h)
                    </label>
                    <input
                      name="office_hourly_rate"
                      value={formData.office_hourly_rate}
                      onChange={handleChange}
                      placeholder="e.g. 30"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Drive hourly rate ($/h)
                    </label>
                    <input
                      name="drive_hourly_rate"
                      value={formData.drive_hourly_rate}
                      onChange={handleChange}
                      placeholder="e.g. 17.75"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Travel cost $/mile
                    </label>
                    <input
                      name="travel_cost_per_km"
                      value={formData.travel_cost_per_km}
                      onChange={handleChange}
                      placeholder="e.g. 0.65"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Bonus ($)
                    </label>
                    <input
                      name="bonus"
                      value={formData.bonus}
                      onChange={handleChange}
                      placeholder="Optional bonus"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Years of experience
                    </label>
                    <input
                      name="years_experience"
                      value={formData.years_experience}
                      onChange={handleChange}
                      placeholder="e.g. 5"
                      type="number"
                      min="0"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Max travel miles
                    </label>
                    <input
                      name="max_travel_miles"
                      value={formData.max_travel_miles}
                      onChange={handleChange}
                      placeholder="e.g. 35"
                      type="number"
                      min="0"
                      step="0.1"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Max caseload
                    </label>
                    <input
                      name="max_caseload"
                      value={formData.max_caseload}
                      onChange={handleChange}
                      placeholder="Number of active children"
                      type="number"
                      min="0"
                      className="border p-2 rounded w-full text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Location (geocoded)
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Latitude
                    </label>
                    <input
                      name="latitude"
                      value={formData.latitude || ''}
                      onChange={handleChange}
                      placeholder="Auto-filled from address, if available"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Longitude
                    </label>
                    <input
                      name="longitude"
                      value={formData.longitude || ''}
                      onChange={handleChange}
                      placeholder="Auto-filled from address, if available"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Contact info */}
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Contact details
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Personal phone
                    </label>
                    <input
                      name="personal_phone"
                      value={formData.personal_phone}
                      onChange={handleChange}
                      placeholder="Teacher's personal phone"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Personal email
                    </label>
                    <input
                      name="personal_email"
                      value={formData.personal_email}
                      onChange={handleChange}
                      placeholder="Teacher's personal email"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Company phone
                    </label>
                    <input
                      name="company_phone"
                      value={formData.company_phone}
                      onChange={handleChange}
                      placeholder="Organization phone for this teacher"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Company email
                    </label>
                    <input
                      name="company_email"
                      value={formData.company_email}
                      onChange={handleChange}
                      placeholder="Organization email for this teacher"
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Employment & notes */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Employment & admin notes
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Employment start date
                    </label>
                    <input
                      type="date"
                      name="employment_start_date"
                      value={formData.employment_start_date || ''}
                      onChange={handleChange}
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Employment end date
                    </label>
                    <input
                      type="date"
                      name="employment_end_date"
                      value={formData.employment_end_date || ''}
                      onChange={handleChange}
                      className="w-full border p-2 rounded text-sm"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="text-[11px] font-medium text-slate-600 block mb-1">
                    Admin notes
                  </label>
                  <textarea
                    name="admin_notes"
                    value={formData.admin_notes}
                    onChange={handleChange}
                    placeholder="Internal notes visible only to admins."
                    className="w-full border p-2 rounded text-sm"
                    rows={2}
                  />
                </div>
              </div>

              {/* Address */}
              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  Address *
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Street
                    </label>
                    <input
                      name="address_street"
                      value={formData.address_street}
                      onChange={handleChange}
                      placeholder="Street and number"
                      className={`border p-2 rounded w-full text-sm ${
                        errors.address_street ? 'border-red-500' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      City
                    </label>
                    <input
                      name="address_city"
                      value={formData.address_city}
                      onChange={handleChange}
                      placeholder="City"
                      className={`border p-2 rounded w-full text-sm ${
                        errors.address_city ? 'border-red-500' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      State
                    </label>
                    <input
                      name="address_state"
                      value={formData.address_state}
                      onChange={handleChange}
                      placeholder="State / Province"
                      className={`border p-2 rounded w-full text-sm ${
                        errors.address_state ? 'border-red-500' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      ZIP
                    </label>
                    <input
                      name="address_zip"
                      value={formData.address_zip}
                      onChange={handleChange}
                      placeholder="ZIP / Postal code"
                      className={`border p-2 rounded w-full text-sm ${
                        errors.address_zip ? 'border-red-500' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 block mb-1">
                      Country
                    </label>
                    <input
                      name="address_country"
                      value={formData.address_country}
                      onChange={handleChange}
                      placeholder="Country"
                      className={`border p-2 rounded w-full text-sm ${
                        errors.address_country ? 'border-red-500' : ''
                      }`}
                    />
                  </div>
                </div>
                {(errors.address_street ||
                  errors.address_city ||
                  errors.address_state ||
                  errors.address_zip ||
                  errors.address_country) && (
                  <div className="text-red-500 text-xs mt-1">
                    Address fields are required.
                  </div>
                )}
              </div>

              {/* Profile */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <h4 className="text-sm font-semibold">
                  Profile (Strengths / Limitations)
                </h4>
                {isEditMode ? (
                  <div className="mt-2">
                    <UserTaxonomyEditor value={taxo} onChange={setTaxo} />
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-green-600">
                    Save the teacher first. You can set Profile on Edit.
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2 text-sm"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 text-sm"
                >
                  <FaSave />
                  {loading ? 'Saving...' : 'Save Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Error Modal */}
      {showImportErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg mb-4 text-red-700">Import Errors</h3>
            <div className="mb-3">
              <span className="text-green-700 font-bold mr-4">
                Imported: {importSuccessCount}
              </span>
              <span className="text-red-700 font-bold">Failed: {importFailCount}</span>
            </div>
            <ul className="text-xs md:text-sm space-y-2 max-h-80 overflow-y-auto">
              {importErrors.map((err, i) => (
                <li key={i} className="border-b pb-1 mb-1">
                  <span className="font-semibold text-red-600">Error:</span>{' '}
                  {err.error}
                  <br />
                  <span className="text-gray-700">{JSON.stringify(err.row)}</span>
                </li>
              ))}
            </ul>
            <button
              className="mt-4 bg-gray-300 px-4 py-2 rounded"
              onClick={() => setShowImportErrorModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {activeTeacherForAudit && !modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[90%] max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Change History</h3>
            <ul className="text-sm space-y-2 max-h-64 overflow-y-auto pr-2">
              {auditLog.length === 0 ? (
                <li className="text-gray-500 italic">No history found.</li>
              ) : (
                auditLog.map((log) => (
                  <li key={log.id} className="text-gray-700">
                    <strong>{log.user_name || 'Unknown'}</strong> – {log.action} –{' '}
                    {String(log.created_at)
                      .replace('T', ' ')
                      .replace('.000Z', '')}
                  </li>
                ))
              )}
            </ul>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setActiveTeacherForAudit(null);
                  setAuditLog([]);
                }}
                className="bg-gray-200 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map modal */}
      {mapModalOpen && mapTeacherLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-[90%] max-w-2xl h-[80vh] relative">
            <h3 className="text-lg font-semibold mb-2">
              Last Location{' '}
              {mapTeacherLocation.name && (
                <span className="text-gray-500">({mapTeacherLocation.name})</span>
              )}
            </h3>
            <div className="w-full h-[65vh] rounded">
              <MapContainer
                center={[mapTeacherLocation.lat, mapTeacherLocation.lon]}
                zoom={15}
                scrollWheelZoom={false}
                style={{
                  width: '100%',
                  height: '60vh',
                  borderRadius: '12px',
                }}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker
                  position={[mapTeacherLocation.lat, mapTeacherLocation.lon]}
                  icon={teacherIcon}
                >
                  <Popup>{mapTeacherLocation.name}</Popup>
                </Marker>
              </MapContainer>
            </div>
            <button
              onClick={() => setMapModalOpen(false)}
              className="absolute top-2 right-2 bg-gray-200 px-3 py-1 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Magic login confirm modal */}
      {loginModalOpen && selectedTeacherForLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[90%] max-w-md">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <FaKey className="text-amber-600" />
              Send login link & QR code
            </h3>
            <p className="text-sm mb-2">
              Do you want to send a secure login link and QR code to:
            </p>
            <p className="font-semibold mb-1">
              {selectedTeacherForLogin.name}
            </p>
            <p className="text-xs text-gray-600 mb-4">
              {selectedTeacherForLogin.email}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              The teacher will receive an email with a one-time magic link and QR code that logs
              them into EarlyCare without sharing a password over the phone.
            </p>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => {
                  setLoginModalOpen(false);
                  setSelectedTeacherForLogin(null);
                }}
                className="bg-gray-200 px-4 py-2 rounded flex items-center gap-2 text-sm"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={handleSendLoginLink}
                disabled={sendingLogin}
                className="bg-amber-600 text-white px-4 py-2 rounded flex items-center gap-2 text-sm"
              >
                <FaKey />
                {sendingLogin ? 'Sending...' : 'Send link & QR'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTeachers;











